import { useMemo, useState } from 'react';
import axios from 'axios';
import { UsersThree, MagnifyingGlass, CheckCircle, WarningCircle } from '@phosphor-icons/react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '@/auth/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Decide whether an employee can get a new account.
function statusOf(emp, takenEmails, linkedEmpIds) {
  const email = (emp.email || '').trim().toLowerCase();
  if (!email) return 'no_email';
  if (linkedEmpIds.has(emp.id) || takenEmails.has(email)) return 'has_account';
  return 'ok';
}

export default function BulkUserDialog({ open, onClose, employees, existingUsers, onCreated }) {
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('user');
  const [selected, setSelected] = useState(() => new Set());
  const [saving, setSaving] = useState(false);

  const takenEmails = useMemo(
    () => new Set((existingUsers || []).map((u) => (u.email || '').toLowerCase()).filter(Boolean)),
    [existingUsers],
  );
  const linkedEmpIds = useMemo(
    () => new Set((existingUsers || []).map((u) => u.employee_id).filter(Boolean)),
    [existingUsers],
  );

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (employees || [])
      .map((e) => ({ emp: e, status: statusOf(e, takenEmails, linkedEmpIds) }))
      .filter(({ emp }) => {
        if (!q) return true;
        return (emp.name || '').toLowerCase().includes(q) || (emp.email || '').toLowerCase().includes(q);
      });
  }, [employees, search, takenEmails, linkedEmpIds]);

  const eligibleIds = useMemo(() => rows.filter((r) => r.status === 'ok').map((r) => r.emp.id), [rows]);
  const allSelected = eligibleIds.length > 0 && eligibleIds.every((id) => selected.has(id));

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (allSelected) {
      const next = new Set(selected);
      eligibleIds.forEach((id) => next.delete(id));
      setSelected(next);
    } else {
      const next = new Set(selected);
      eligibleIds.forEach((id) => next.add(id));
      setSelected(next);
    }
  };

  const reset = () => { setSelected(new Set()); setSearch(''); setRole('user'); };

  const submit = async () => {
    const ids = [...selected];
    if (ids.length === 0) return toast.error('登録する社員を選択してください');
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/users/bulk`, { employee_ids: ids, role });
      const made = (data.created || []).length;
      const skip = (data.skipped || []).length;
      toast.success(`${made}名のアカウントを作成しました${skip ? `（${skip}名スキップ）` : ''}`);
      reset();
      onCreated?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || '一括登録に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const okCount = eligibleIds.length;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose?.(); } }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col" data-testid="bulk-user-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UsersThree size={20} weight="duotone" className="text-teal-700" />
            アカウントを一括登録
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs text-zinc-500 leading-relaxed">
          社員ディレクトリから選択した人に、ランダムな初期パスワードでログインアカウントを作成します。
          各ユーザーは初回ログイン時にパスワードの変更が求められます。メール未登録・登録済みの社員は選択できません。
        </p>

        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="氏名・メールで検索"
              className="pl-9 h-9"
              data-testid="bulk-search"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-zinc-600 shrink-0">権限</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="w-32 h-9" data-testid="bulk-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="user">ユーザー</SelectItem>
                <SelectItem value="manager">管理者</SelectItem>
                <SelectItem value="admin">admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center justify-between text-xs px-1">
          <button
            type="button"
            onClick={toggleAll}
            disabled={okCount === 0}
            data-testid="bulk-select-all"
            className="font-semibold text-teal-700 hover:underline disabled:opacity-40 disabled:no-underline"
          >
            {allSelected ? 'すべて解除' : `登録可能な${okCount}名を全選択`}
          </button>
          <span className="text-zinc-500">選択中: <span className="font-semibold text-zinc-900">{selected.size}</span> 名</span>
        </div>

        <div className="flex-1 overflow-y-auto rounded-lg border border-zinc-200 divide-y divide-zinc-100" data-testid="bulk-employee-list">
          {rows.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-zinc-400">該当する社員がいません。</p>
          ) : rows.map(({ emp, status }) => {
            const disabled = status !== 'ok';
            const checked = selected.has(emp.id);
            return (
              <label
                key={emp.id}
                data-testid={`bulk-emp-${emp.id}`}
                className={`flex items-center gap-3 px-3 py-2.5 ${disabled ? 'opacity-60 cursor-not-allowed bg-zinc-50/60' : 'cursor-pointer hover:bg-blue-50/40'}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => toggle(emp.id)}
                  data-testid={`bulk-check-${emp.id}`}
                  className="w-4 h-4 accent-[#0052FF] shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-zinc-900 truncate">
                    {emp.name} {emp.code ? <span className="text-[11px] text-zinc-400">({emp.code})</span> : null}
                  </p>
                  <p className="text-[11px] text-zinc-500 truncate">{emp.email || 'メールアドレス未登録'}</p>
                </div>
                {status === 'ok' && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 shrink-0">
                    <CheckCircle size={11} weight="fill" /> 登録可
                  </span>
                )}
                {status === 'has_account' && (
                  <span className="text-[10px] font-semibold text-zinc-500 bg-zinc-100 border border-zinc-200 rounded-full px-2 py-0.5 shrink-0">登録済み</span>
                )}
                {status === 'no_email' && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 shrink-0">
                    <WarningCircle size={11} weight="fill" /> メール未登録
                  </span>
                )}
              </label>
            );
          })}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => { reset(); onClose?.(); }} disabled={saving} className="w-full sm:w-auto">
            キャンセル
          </Button>
          <Button onClick={submit} disabled={saving || selected.size === 0} data-testid="bulk-submit" className="bg-teal-700 hover:bg-teal-800 w-full sm:w-auto">
            {saving ? '登録中…' : `選択した${selected.size}名を登録`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
